import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getCrmColumns, queryCrm, getCrmCustomer, startCrmRefresh, getCrmRefreshStatus,
  exportCrmView,
} from '../api/crm';

const COLUMNS_STORAGE_KEY = 'zocs_crm_columns';
const PANELS_STORAGE_KEY = 'zocs_crm_filter_panels';
const PAGE_SIZE = 50;

const TAG_STYLE = {
  transacted: 'bg-green-100 text-green-800 border-green-300',
  future_booking: 'bg-blue-100 text-blue-800 border-blue-300',
  past_booked: 'bg-amber-100 text-amber-800 border-amber-300',
  app_only: 'bg-purple-100 text-purple-800 border-purple-300',
  lead_only: 'bg-gray-100 text-gray-700 border-gray-300',
};

const pad = (n) => String(n).padStart(2, '0');
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return isoDay(d); };

function fmtDate(v) {
  if (!v) return '';
  const [y, m, d] = v.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}
function fmtDateTime(v) {
  if (!v) return '';
  return `${fmtDate(v)} ${v.slice(11, 16)}`;
}
function fmtCell(col, v, tagLabels) {
  if (v === null || v === undefined || v === '') return '';
  if (col.key === 'tag') return tagLabels[v] || v;
  if (col.key === 'lifetime_gmv') return `₹${Math.round(v).toLocaleString('en-IN')}`;
  if (col.type === 'date') return fmtDate(v);
  if (col.type === 'datetime') return fmtDateTime(v);
  if (col.type === 'bool') return v ? '✓' : '';
  return String(v);
}
function ago(iso) {
  if (!iso) return 'never';
  const mins = Math.round((Date.now() - new Date(`${iso}Z`).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 1440)} d ago`;
}
function isActive(f) {
  if (!f) return false;
  return (f.min !== undefined && f.min !== '') || (f.max !== undefined && f.max !== '')
    || (f.values && f.values.length) || (f.text && f.text.trim())
    || f.value === true || f.value === false || f.empty === true || f.empty === false;
}
function cleanFilters(filters) {
  const out = {};
  Object.entries(filters).forEach(([k, f]) => { if (isActive(f)) out[k] = f; });
  return out;
}
// ── one filter panel, control chosen by column type ─────────────────────────
function FilterControl({ col, value, options, tagLabels, onChange, onRemove }) {
  const f = value || {};
  const set = (patch) => onChange({ ...f, ...patch });
  const inputCls = 'border border-gray-300 rounded px-1.5 py-1 text-sm w-full min-w-0';

  let body = null;
  if (col.type === 'number') {
    body = (
      <div className="flex gap-2">
        <input type="number" placeholder="min" className={inputCls} value={f.min ?? ''} onChange={(e) => set({ min: e.target.value })} />
        <input type="number" placeholder="max" className={inputCls} value={f.max ?? ''} onChange={(e) => set({ max: e.target.value })} />
      </div>
    );
  } else if (col.type === 'date' || col.type === 'datetime') {
    body = (
      <div>
        <div className="flex gap-2">
          <input type="date" className={inputCls} value={f.min ?? ''} onChange={(e) => set({ min: e.target.value })} />
          <input type="date" className={inputCls} value={f.max ?? ''} onChange={(e) => set({ max: e.target.value })} />
        </div>
        <div className="flex gap-1 mt-1 flex-wrap">
          {[['7d', 7], ['30d', 30], ['90d', 90]].map(([l, n]) => (
            <button key={l} type="button" onClick={() => set({ min: daysAgo(n), max: isoDay(new Date()) })}
              className="text-xs px-2 py-0.5 rounded border border-gray-300 hover:bg-gray-100">Last {l}</button>
          ))}
          {col.key === 'next_booking_date' && (
            <button type="button" onClick={() => set({ min: isoDay(new Date()), max: '' })}
              className="text-xs px-2 py-0.5 rounded border border-gray-300 hover:bg-gray-100">Today onwards</button>
          )}
        </div>
      </div>
    );
  } else if (col.type === 'enum' || col.type === 'tags') {
    const opts = options || [];
    const sel = new Set(f.values || []);
    body = (
      <div className="max-h-32 overflow-y-auto border border-gray-200 rounded p-1">
        {opts.length === 0 && <div className="text-xs text-gray-400 px-1">No values</div>}
        {opts.map((o) => (
          <label key={o} className="flex items-center gap-2 text-sm px-1 py-0.5 hover:bg-gray-50 cursor-pointer">
            <input type="checkbox" checked={sel.has(o)} onChange={() => {
              const next = new Set(sel); if (next.has(o)) next.delete(o); else next.add(o);
              set({ values: Array.from(next) });
            }} />
            <span className="truncate">{col.key === 'tag' ? tagLabels[o] : o}</span>
          </label>
        ))}
      </div>
    );
  } else if (col.type === 'text') {
    body = <input type="text" placeholder="contains…" className={inputCls} value={f.text ?? ''} onChange={(e) => set({ text: e.target.value })} />;
  } else if (col.type === 'bool') {
    body = (
      <select className={inputCls} value={f.value === true ? 'yes' : f.value === false ? 'no' : ''}
        onChange={(e) => set({ value: e.target.value === '' ? undefined : e.target.value === 'yes' })}>
        <option value="">Any</option><option value="yes">Yes</option><option value="no">No</option>
      </select>
    );
  }

  return (
    <div className={`w-64 shrink-0 p-2 rounded border bg-white ${isActive(f) ? 'border-zoddle-green bg-green-50' : 'border-gray-300'}`}>
      <div className="flex items-center justify-between mb-1 gap-2">
        <span className="text-xs font-semibold text-gray-700 truncate">{col.label}</span>
        <div className="flex items-center gap-1 shrink-0">
          {col.type !== 'bool' && (
            <select className="text-xs border border-gray-200 rounded px-1"
              value={f.empty === true ? 'blank' : f.empty === false ? 'filled' : ''}
              onChange={(e) => set({ empty: e.target.value === '' ? undefined : e.target.value === 'blank' })}>
              <option value="">any</option><option value="filled">not blank</option><option value="blank">blank</option>
            </select>
          )}
          <button type="button" onClick={onRemove} title="Remove filter" className="text-sm text-gray-400 hover:text-zoddle-pink px-1">✕</button>
        </div>
      </div>
      {body}
    </div>
  );
}

// ── generic renderer for the per-source details blob ─────────────────────────
function DetailsValue({ v }) {
  if (v === null || v === undefined || v === '') return <span className="text-gray-300">—</span>;
  if (Array.isArray(v)) {
    if (!v.length) return <span className="text-gray-300">none</span>;
    if (typeof v[0] !== 'object') return <span>{v.join(', ')}</span>;
    const keys = Array.from(new Set(v.flatMap((r) => Object.keys(r || {}))));
    return (
      <div className="overflow-x-auto">
        <table className="text-xs border border-gray-200">
          <thead className="bg-gray-50"><tr>{keys.map((k) => <th key={k} className="px-2 py-1 text-left font-medium whitespace-nowrap">{k}</th>)}</tr></thead>
          <tbody>
            {v.map((r, i) => (
              <tr key={i} className="border-t border-gray-100">
                {keys.map((k) => (
                  <td key={k} className="px-2 py-1 align-top whitespace-nowrap max-w-xs truncate">
                    {typeof r?.[k] === 'object' && r?.[k] !== null ? JSON.stringify(r[k]) : String(r?.[k] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (typeof v === 'object') {
    return (
      <div className="space-y-1">
        {Object.entries(v).map(([k, x]) => (
          <div key={k} className="flex gap-2 text-xs">
            <span className="text-gray-500 min-w-[9rem] shrink-0">{k}</span>
            <div className="min-w-0"><DetailsValue v={x} /></div>
          </div>
        ))}
      </div>
    );
  }
  return <span>{String(v)}</span>;
}

function CustomerDrawer({ phone, columns, tagLabels, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    setData(null); setErr('');
    getCrmCustomer(phone).then(setData).catch((e) => setErr(e.response?.data?.detail || 'Failed to load'));
  }, [phone]);
  const groups = useMemo(() => {
    const g = {};
    columns.forEach((c) => { (g[c.group] = g[c.group] || []).push(c); });
    return g;
  }, [columns]);
  const sources = [['zocs', 'ZOCS'], ['app', 'Customer app'], ['leads', 'Leads sheet']];

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div className="w-full max-w-4xl bg-white h-full overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold">{data?.name || phone}</h2>
            <div className="text-sm text-gray-500">{phone}</div>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>
        {err && <div className="text-zoddle-pink text-sm">{err}</div>}
        {!data && !err && <div className="text-sm text-gray-400">Loading…</div>}
        {data && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {Object.entries(groups).map(([group, cols]) => (
                <div key={group} className="border border-gray-200 rounded p-3">
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">{group}</div>
                  {cols.map((c) => (
                    <div key={c.key} className="flex justify-between gap-3 text-sm py-0.5">
                      <span className="text-gray-500">{c.label}</span>
                      <span className="text-right break-words min-w-0">{fmtCell(c, data[c.key], tagLabels) || <span className="text-gray-300">—</span>}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className="mb-4">
              <div className="text-sm font-semibold mb-2">Kids</div>
              <DetailsValue v={data.details?.kids} />
            </div>
            {sources.map(([k, label]) => data.details?.[k] && (
              <div key={k} className="mb-5">
                <div className="text-sm font-semibold mb-2">{label}</div>
                <div className="border border-gray-200 rounded p-3"><DetailsValue v={data.details[k]} /></div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────
export default function CRM() {
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'last_order_date', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ total: 0, rows: [], per_tag: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [visibleCols, setVisibleCols] = useState(null);
  const [showCols, setShowCols] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  const [panels, setPanels] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(PANELS_STORAGE_KEY) || '[]');
      return Array.isArray(saved) ? saved : [];
    } catch (_) { return []; }
  });
  const [refresh, setRefresh] = useState(null);
  const [openPhone, setOpenPhone] = useState(null);
  const [exportingView, setExportingView] = useState(false);
  const reqId = useRef(0);

  const loadMeta = useCallback(() => getCrmColumns().then((m) => {
    setMeta(m);
    setVisibleCols((prev) => {
      if (prev) return prev;
      try {
        const saved = JSON.parse(localStorage.getItem(COLUMNS_STORAGE_KEY) || 'null');
        if (Array.isArray(saved) && saved.length) return new Set(saved.filter((k) => m.columns.some((c) => c.key === k)));
      } catch (_) { /* ignore */ }
      return new Set(m.columns.filter((c) => c.default).map((c) => c.key));
    });
  }).catch((e) => setError(e.response?.data?.detail || 'Failed to load columns')), []);

  const loadStatus = useCallback(() => getCrmRefreshStatus().then(setRefresh).catch(() => {}), []);

  useEffect(() => { loadMeta(); loadStatus(); }, [loadMeta, loadStatus]);

  useEffect(() => {
    if (!visibleCols) return;
    try { localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(Array.from(visibleCols))); } catch (_) { /* ignore */ }
  }, [visibleCols]);

  useEffect(() => {
    try { localStorage.setItem(PANELS_STORAGE_KEY, JSON.stringify(panels)); } catch (_) { /* ignore */ }
  }, [panels]);

  const active = useMemo(() => cleanFilters(filters), [filters]);

  const runQuery = useCallback(() => {
    const id = ++reqId.current;
    setLoading(true); setError('');
    queryCrm({ filters: active, search, sort: sort.key, sort_dir: sort.dir, page, page_size: PAGE_SIZE })
      .then((r) => { if (id === reqId.current) setResult(r); })
      .catch((e) => { if (id === reqId.current) setError(e.response?.data?.detail || 'Query failed'); })
      .finally(() => { if (id === reqId.current) setLoading(false); });
  }, [active, search, sort, page]);

  useEffect(() => {
    if (!meta) return undefined;
    const t = setTimeout(runQuery, 350);
    return () => clearTimeout(t);
  }, [meta, runQuery]);

  // poll while a refresh is running, then reload everything
  const running = refresh?.latest?.status === 'running';
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(async () => {
      const s = await getCrmRefreshStatus().catch(() => null);
      if (!s) return;
      setRefresh(s);
      if (s.latest?.status !== 'running') { loadMeta(); runQuery(); }
    }, 2000);
    return () => clearInterval(t);
  }, [running, loadMeta, runQuery]);

  const doRefresh = async () => {
    try { await startCrmRefresh(); loadStatus(); } catch (e) { alert(e.response?.data?.detail || 'Could not start refresh'); }
  };

  const setFilter = (key, f) => { setFilters((prev) => ({ ...prev, [key]: f })); setPage(1); };
  const toggleTag = (t) => {
    const cur = new Set(filters.tag?.values || []);
    if (cur.has(t)) cur.delete(t); else cur.add(t);
    setFilter('tag', { values: Array.from(cur) });
  };
  const addPanel = (key) => setPanels((p) => (p.includes(key) ? p : [...p, key]));
  const removePanel = (key) => { setPanels((p) => p.filter((k) => k !== key)); setFilter(key, {}); };
  const clearAll = () => {
    setFilters((prev) => (prev.tag ? { tag: prev.tag } : {}));
    setPanels([]); setSearch(''); setPage(1);
  };
  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));
    setPage(1);
  };

  if (!meta || !visibleCols) {
    return <div className="p-6 text-sm text-gray-500">{error || 'Loading CRM…'}</div>;
  }

  const colByKey = Object.fromEntries(meta.columns.map((c) => [c.key, c]));
  const shownCols = meta.columns.filter((c) => visibleCols.has(c.key));
  const groups = meta.columns.reduce((g, c) => { (g[c.group] = g[c.group] || []).push(c); return g; }, {});
  const fq = filterSearch.trim().toLowerCase();
  const filterGroups = meta.columns
    .filter((c) => c.key !== 'tag' && (!fq || c.label.toLowerCase().includes(fq)))
    .reduce((g, c) => { (g[c.group] = g[c.group] || []).push(c); return g; }, {});
  const shownPanels = panels.map((k) => colByKey[k]).filter((c) => c && c.key !== 'tag');
  const pages = Math.max(1, Math.ceil(result.total / PAGE_SIZE));
  const selectedTags = new Set(filters.tag?.values || []);
  const lastOk = refresh?.last_success;
  const latest = refresh?.latest;
  const exportBody = { filters: active, search };

  return (
    <div className="p-4 max-w-full">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-semibold">CRM</h1>
          <div className="text-xs text-gray-500">
            {running ? 'Refreshing…' : `Last refreshed ${ago(lastOk?.finished_at)}`}
            {latest?.status === 'failed' && <span className="text-zoddle-pink"> · last refresh failed: {latest.error}</span>}
            {lastOk?.counts?.warnings?.length > 0 && <span className="text-amber-600"> · {lastOk.counts.warnings.join('; ')}</span>}
          </div>
        </div>
        <button type="button" onClick={doRefresh} disabled={running}
          className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50">
          {running ? 'Refreshing…' : '↻ Refresh CRM'}
        </button>
      </div>

      {/* tag chips */}
      <div className="flex flex-wrap gap-2 mb-3">
        {meta.tag_order.map((t) => (
          <button key={t} type="button" onClick={() => toggleTag(t)}
            className={`px-3 py-1 rounded-full border text-sm ${TAG_STYLE[t]} ${selectedTags.has(t) ? 'ring-2 ring-offset-1 ring-gray-500' : 'opacity-80 hover:opacity-100'}`}>
            {meta.tag_labels[t]} <b>{(result.per_tag?.[t] ?? 0).toLocaleString('en-IN')}</b>
          </button>
        ))}
      </div>

      {/* toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <input type="text" placeholder="Search phone or name…" value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded px-2 py-1.5 text-sm w-56" />
        <div className="relative">
          <button type="button" onClick={() => { setShowFilterMenu((v) => !v); setShowCols(false); }}
            className={`px-3 py-1.5 text-sm rounded border ${showFilterMenu ? 'bg-gray-100 border-gray-400' : 'border-gray-300'}`}>
            Filters{shownPanels.length ? ` (${shownPanels.length})` : ''} ▾
          </button>
          {showFilterMenu && (
            <div className="absolute z-40 mt-1 bg-white border border-gray-200 rounded shadow-lg w-72">
              <div className="p-2 border-b border-gray-100 flex gap-2">
                <input type="text" autoFocus placeholder="Search fields…" value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm flex-1" />
                <button type="button" className="text-xs text-gray-500" onClick={() => setShowFilterMenu(false)}>Close</button>
              </div>
              <div className="max-h-96 overflow-y-auto p-2">
                {Object.entries(filterGroups).map(([g, cols]) => (
                  <div key={g} className="mb-2">
                    <div className="text-xs font-semibold text-gray-500 uppercase">{g}</div>
                    {cols.map((c) => {
                      const added = panels.includes(c.key);
                      return (
                        <div key={c.key} className="flex items-center justify-between text-sm py-0.5">
                          <span className={added ? 'text-gray-400' : ''}>{c.label}</span>
                          {added
                            ? <span className="text-xs text-gray-400">Added</span>
                            : <button type="button" onClick={() => addPanel(c.key)}
                                className="text-xs px-2 py-0.5 rounded border border-zoddle-green text-zoddle-green hover:bg-green-50">+ Add</button>}
                        </div>
                      );
                    })}
                  </div>
                ))}
                {Object.keys(filterGroups).length === 0 && <div className="text-xs text-gray-400">No matching fields</div>}
              </div>
            </div>
          )}
        </div>
        <div className="relative">
          <button type="button" onClick={() => { setShowCols((v) => !v); setShowFilterMenu(false); }} className="px-3 py-1.5 text-sm rounded border border-gray-300">Columns ▾</button>
          {showCols && (
            <div className="absolute z-40 mt-1 bg-white border border-gray-200 rounded shadow-lg p-3 w-72 max-h-96 overflow-y-auto">
              <div className="flex justify-between mb-2 text-xs">
                <button type="button" className="text-blue-600" onClick={() => setVisibleCols(new Set(meta.columns.map((c) => c.key)))}>All</button>
                <button type="button" className="text-blue-600" onClick={() => setVisibleCols(new Set(meta.columns.filter((c) => c.default).map((c) => c.key)))}>Default</button>
                <button type="button" className="text-gray-500" onClick={() => setShowCols(false)}>Close</button>
              </div>
              {Object.entries(groups).map(([g, cols]) => (
                <div key={g} className="mb-2">
                  <div className="text-xs font-semibold text-gray-500 uppercase">{g}</div>
                  {cols.map((c) => (
                    <label key={c.key} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
                      <input type="checkbox" checked={visibleCols.has(c.key)} onChange={() => setVisibleCols((prev) => {
                        const n = new Set(prev); if (n.has(c.key)) n.delete(c.key); else n.add(c.key); return n;
                      })} />
                      {c.label}
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex-1" />
        <span className="text-sm text-gray-600">{loading ? 'Loading…' : `${result.total.toLocaleString('en-IN')} customers`}</span>
        <button type="button" disabled={!result.total || exportingView}
          onClick={async () => {
            setExportingView(true);
            try {
              await exportCrmView({ ...exportBody, columns: shownCols.map((c) => c.key), sort: sort.key, sort_dir: sort.dir });
            } catch (e) { alert(e.message || 'Export failed'); }
            setExportingView(false);
          }}
          className="px-3 py-1.5 text-sm rounded bg-gray-800 text-white disabled:opacity-40">{exportingView ? 'Exporting…' : 'Export view CSV'}</button>      </div>

      {/* added filter panels, fixed under the ribbon */}
      {(shownPanels.length > 0 || search) && (
        <div className="flex flex-wrap items-start gap-2 mb-3">
          {shownPanels.map((c) => (
            <FilterControl key={c.key} col={c} value={filters[c.key]} options={meta.options[c.key]}
              tagLabels={meta.tag_labels} onChange={(f) => setFilter(c.key, f)} onRemove={() => removePanel(c.key)} />
          ))}
          <button type="button" onClick={clearAll} className="text-xs text-gray-500 underline self-center">Clear all</button>
        </div>
      )}

      {error && <div className="text-sm text-zoddle-pink mb-2">{error}</div>}

      {/* table */}
      <div className="overflow-x-auto border border-gray-200 rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {shownCols.map((c) => (
                <th key={c.key} onClick={() => toggleSort(c.key)}
                  className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap cursor-pointer select-none hover:bg-gray-100">
                  {c.label}{sort.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.rows.map((r) => (
              <tr key={r.phone} onClick={() => setOpenPhone(r.phone)} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer">
                {shownCols.map((c) => (
                  <td key={c.key} className="px-3 py-1.5 whitespace-nowrap max-w-xs truncate" title={typeof r[c.key] === 'string' ? r[c.key] : undefined}>
                    {c.key === 'tag'
                      ? <span className={`px-2 py-0.5 rounded-full border text-xs ${TAG_STYLE[r.tag]}`}>{meta.tag_labels[r.tag]}</span>
                      : fmtCell(c, r[c.key], meta.tag_labels)}
                  </td>
                ))}
              </tr>
            ))}
            {!loading && result.rows.length === 0 && (
              <tr><td colSpan={shownCols.length} className="px-3 py-6 text-center text-gray-400">
                {lastOk ? 'No customers match these filters.' : 'CRM is empty. Click "Refresh CRM" to build it.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-end gap-2 mt-2 text-sm">
        <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40">‹ Prev</button>
        <span>Page {page} of {pages}</span>
        <button type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40">Next ›</button>
      </div>

      {openPhone && <CustomerDrawer phone={openPhone} columns={meta.columns} tagLabels={meta.tag_labels} onClose={() => setOpenPhone(null)} />}
    </div>
  );
}
